import { FC, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as ArrowRightIcon } from 'src/assets/svg/arrow-right-line.svg'
import { ReactComponent as ArrowRightNavIcon } from 'src/assets/svg/arrow-right.svg'
import { ReactComponent as CampaignIcon } from 'src/assets/svg/camp-card.svg'
import { ReactComponent as MentionIcon } from 'src/assets/svg/akar-icons_mention.svg'
import { ReactComponent as SentimentIcon } from 'src/assets/svg/Vector.svg'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { Skeleton } from 'src/components/Skeleton'
import en from 'src/constants/en'
import { CAMPAIGNS_ROUTE } from 'src/constants/routes'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { useGetOverviewQuery } from 'src/reactQuery/useGetOverviewQuery'
import { CampaignHealthCard } from './CampaignHealthCard'
import styles from './overviewSection.module.scss'

// Health cards are 392x207 with a 12px gap (see campaignHealthCard.module.scss).
// The skeleton mirrors those exact dimensions so the row reserves its real
// height up front — without this the container is empty while loading and the
// section collapses, then jumps open when the data lands.
const HEALTH_CARD_WIDTH = 392
const HEALTH_CARD_HEIGHT = 207
const HEALTH_CARD_RADIUS = 16
const HEALTH_SKELETON_COUNT = 3

interface OverviewSectionProps {
  endDate?: string
  isLifetime?: boolean
  startDate?: string
}

export const OverviewSection: FC<OverviewSectionProps> = ({
  endDate,
  isLifetime = false,
  startDate,
}) => {
  const navigate = useNavigate()
  const campaignsLink = useCustomLink(CAMPAIGNS_ROUTE)

  const { data: overviewData, isFetching } = useGetOverviewQuery({
    end: endDate,
    start: startDate,
  })

  // Skeletons are for the FIRST load only — when there is genuinely nothing to
  // show. A refetch (e.g. changing the date range) keeps the previous values on
  // screen and dims them instead, which avoids flashing the whole section back
  // to placeholders for data that is already there.
  const isInitialLoading = isFetching && !overviewData

  const activeCampaigns = overviewData?.summary?.activeCampaigns || 0
  const campaignHealthData = overviewData?.campaignHealth || []
  const mentionsTrend = overviewData?.summary?.mentionsTrend || 0
  const negativePerc = overviewData?.summary?.negativePerc || 0
  const negatives = overviewData?.summary?.negatives || 0
  const neutralPerc = overviewData?.summary?.neutralPerc || 0
  const neutrals = overviewData?.summary?.neutrals || 0
  const positivePerc = overviewData?.summary?.positivePerc || 0
  const positives = overviewData?.summary?.positives || 0
  const totalCampaigns = overviewData?.summary?.totalCampaigns || 0
  const totalMentions = overviewData?.summary?.totalMentions || 0

  const cardsContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)
  const [scrollProgress, setScrollProgress] = useState(0)

  const handleScroll = () => {
    if (cardsContainerRef.current) {
      const { clientWidth, scrollLeft, scrollWidth } = cardsContainerRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10)

      const maxScroll = scrollWidth - clientWidth
      const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0
      setScrollProgress(progress)
    }
  }

  const handleScrollRight = () => {
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollBy({
        behavior: 'smooth',
        left: 404, // card width (392) + gap (12)
      })
    }
  }

  const handleScrollLeft = () => {
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollBy({
        behavior: 'smooth',
        left: -404, // card width (392) + gap (12)
      })
    }
  }

  const formatNumber = (num: number): string => {
    return num.toLocaleString()
  }

  const formatPercentage = (num: number): string => {
    return `${Math.round(num)}%`
  }

  return (
    <div className={styles.overviewSection}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>Overview</h2>
          {/* On first load the skeletons already say "loading"; the dots would
              be redundant. Keep them for refetches, where real values stay up. */}
          {isFetching && !isInitialLoading && (
            <div className={styles.loader}>
              <div />
              <div />
              <div />
              <div />
            </div>
          )}
        </div>
      </div>

      <div
        className={styles.metricsGrid}
        // Dim only when refetching over existing values — dimming the skeletons
        // themselves would just wash out the shimmer.
        style={{ opacity: isFetching && !isInitialLoading ? 0.5 : 1 }}
      >
        {/* Total Mentions Card */}
        <div className={styles.metricCard}>
          <div className={styles.cardHeader}>
            <MentionIcon className={styles.cardIcon} />
            <span className={styles.cardTitle}>Total Mentions</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.mainValue}>
              {isInitialLoading ? (
                <Skeleton height={32} width={132} />
              ) : (
                formatNumber(totalMentions)
              )}
            </div>
            {!isLifetime && mentionsTrend !== 0 && (
              <div className={styles.trendSection}>
                <div
                  className={`${styles.trendBadge} ${mentionsTrend < 0 ? styles.negative : ''}`}
                >
                  <TrendUpIcon
                    className={styles.trendIcon}
                    style={
                      mentionsTrend < 0
                        ? { transform: 'rotate(180deg)' }
                        : undefined
                    }
                  />
                  <span className={styles.trendValue}>
                    {formatPercentage(Math.abs(mentionsTrend))}
                  </span>
                </div>
                <span className={styles.trendLabel}>vs previous period</span>
              </div>
            )}
          </div>
        </div>

        {/* Sentiment Card */}
        <div className={styles.metricCard}>
          <div className={styles.cardHeader}>
            <SentimentIcon className={styles.cardIcon} />
            <span className={styles.cardTitle}>Sentiment</span>
          </div>
          <div className={styles.sentimentContent}>
            <div className={styles.sentimentRow}>
              <div className={styles.barContainer}>
                <div
                  className={`${styles.bar} ${styles.positiveBar}`}
                  style={{ width: `${positivePerc}%` }}
                />
              </div>
              <div className={styles.sentimentInfo}>
                <div className={styles.sentimentText}>
                  {isInitialLoading ? (
                    <Skeleton height={14} width={36} />
                  ) : (
                    <span className={styles.percentValue}>
                      {formatPercentage(positivePerc)}
                    </span>
                  )}
                  <span className={styles.sentimentType}>Positive</span>
                </div>
                {isInitialLoading ? (
                  <Skeleton height={12} width={35} />
                ) : (
                  <span className={styles.sentimentNumber}>
                    {formatNumber(positives)}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.sentimentRow}>
              <div className={styles.barContainer}>
                <div
                  className={`${styles.bar} ${styles.neutralBar}`}
                  style={{ width: `${neutralPerc}%` }}
                />
              </div>
              <div className={styles.sentimentInfo}>
                <div className={styles.sentimentText}>
                  {isInitialLoading ? (
                    <Skeleton height={14} width={36} />
                  ) : (
                    <span className={styles.percentValue}>
                      {formatPercentage(neutralPerc)}
                    </span>
                  )}
                  <span className={styles.sentimentType}>Neutral</span>
                </div>
                {isInitialLoading ? (
                  <Skeleton height={12} width={35} />
                ) : (
                  <span className={styles.sentimentNumber}>
                    {formatNumber(neutrals)}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.sentimentRow}>
              <div className={styles.barContainer}>
                <div
                  className={`${styles.bar} ${styles.negativeBar}`}
                  style={{ width: `${negativePerc}%` }}
                />
              </div>
              <div className={styles.sentimentInfo}>
                <div className={styles.sentimentText}>
                  {isInitialLoading ? (
                    <Skeleton height={14} width={36} />
                  ) : (
                    <span className={styles.percentValue}>
                      {formatPercentage(negativePerc)}
                    </span>
                  )}
                  <span className={styles.sentimentType}>Negative</span>
                </div>
                {isInitialLoading ? (
                  <Skeleton height={12} width={35} />
                ) : (
                  <span className={styles.sentimentNumber}>
                    {formatNumber(negatives)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns Card */}
        <div className={styles.metricCard}>
          <div className={styles.cardHeader}>
            <CampaignIcon className={styles.cardIcon} />
            <span className={styles.cardTitle}>{en.campaigns}</span>
          </div>
          <div className={styles.campaignContent}>
            <div className={styles.campaignStats}>
              {isInitialLoading ? (
                <>
                  <Skeleton height={28} width={124} />
                  <Skeleton height={14} width={92} />
                </>
              ) : (
                <>
                  <div className={styles.campaignMainValue}>
                    {activeCampaigns} Active
                  </div>
                  <div className={styles.subValue}>
                    out of {totalCampaigns} total
                  </div>
                </>
              )}
            </div>
            <button
              className={styles.viewAllButton}
              onClick={() => navigate(campaignsLink)}
            >
              All Campaigns
              <ArrowRightIcon className={styles.viewAllArrow} />
            </button>
          </div>
        </div>
      </div>

      {/* Campaign Health Overview */}
      <div className={styles.campaignHealth}>
        <h3 className={styles.sectionSubtitle}>Campaign Health Overview</h3>
        {(() => {
          const showNavigation = campaignHealthData.length >= 3

          return (
            <>
              <div
                className={`${styles.cardsWrapper} ${showLeftArrow && showNavigation ? styles.cardsWrapperWithLeftGradient : ''} ${!showRightArrow && showNavigation ? styles.cardsWrapperNoRightGradient : ''}`}
              >
                {showLeftArrow && showNavigation && (
                  <button
                    className={`${styles.navButton} ${styles.navButtonLeft}`}
                    onClick={handleScrollLeft}
                  >
                    <ArrowRightNavIcon
                      className={`${styles.navArrow} ${styles.navArrowLeft}`}
                    />
                  </button>
                )}
                <div
                  className={styles.cardsContainer}
                  onScroll={handleScroll}
                  ref={cardsContainerRef}
                >
                  {isInitialLoading &&
                    Array.from({ length: HEALTH_SKELETON_COUNT }).map(
                      (_, index) => (
                        <Skeleton
                          height={HEALTH_CARD_HEIGHT}
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          radius={HEALTH_CARD_RADIUS}
                          width={HEALTH_CARD_WIDTH}
                        />
                      )
                    )}
                  {campaignHealthData.map((campaign) => (
                    <CampaignHealthCard
                      id={campaign.id}
                      isLifetime={isLifetime}
                      key={campaign.id}
                      mentions={campaign.mentions}
                      name={campaign.name}
                      negativePerc={campaign.negativePerc}
                      neutralPerc={campaign.neutralPerc}
                      positivePerc={campaign.positivePerc}
                      trend={campaign.trend}
                    />
                  ))}
                </div>
                {showRightArrow && showNavigation && (
                  <button
                    className={styles.navButton}
                    onClick={handleScrollRight}
                  >
                    <ArrowRightNavIcon className={styles.navArrow} />
                  </button>
                )}
              </div>
              {showNavigation && (
                <div className={styles.scrollbarTrack}>
                  <div
                    className={styles.scrollbarThumb}
                    style={{ left: `${scrollProgress * (100 - 43)}%` }}
                  />
                </div>
              )}
            </>
          )
        })()}
      </div>
    </div>
  )
}
